import { gql, useQuery } from '@apollo/client'
import _ from 'lodash'
import moment from 'moment'
import { useMemo } from 'react'

import { useDateFilter } from '../../../../dateFilter'
import { useGroupFilter } from '../../../../groupFilter'
import format from '../../../../utils/format'
import { corporateGroupTableConfigs, useVariables } from '../../../../variables'
import { IApiDataType } from '../../../types'

type IMetricDataType = Record<
  string,
  {
    name: string
    unit: 'CENT' | 'PERCENTAGE' | 'DOLLAR' | 'COUNT' | 'SECONDS' | 'SCORE'
    value: number
  }
>

type IDataType<
  T extends string =
    | 'listLocationGroupMetricValues'
    | 'listLocationMetricValues',
> = Record<
  T,
  {
    nodes: ((T extends 'listLocationGroupMetricValues'
      ? {
          locationGroupId: number
        }
      : {
          locationId: number
        }) &
      Record<'metricData', IMetricDataType>)[]
  }
> &
  Record<
    T extends 'listLocationGroupMetricValues'
      ? 'listLocationGroupDisputes'
      : 'listLocationDisputes',
    {
      nodes: ((T extends 'listLocationGroupMetricValues'
        ? {
            locationGroupId: number
          }
        : {
            locationId: number
          }) & {
        startDate: string
        endDate: string
        totalDisputeCount: string
        totalDisputeAmount: string
        completedDisputeCount: string
        completedDisputeAmount: string
        pendingDisputeCount: string
        pendingDisputeAmount: string
        lostDisputeCount: string
        lostDisputeAmount: string
      })[]
    }
  >

const query = gql`
  query disputeMetrics(
    $iEndDate: Date!
    $iStartDate: Date!
    $iFilter: JSON!
    $hasGroupBy: Boolean!
  ) {
    listLocationDisputes(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iFilter: $iFilter
    ) @include(if: $hasGroupBy) {
      nodes {
        startDate
        endDate
        locationId
        totalDisputeCount
        totalDisputeAmount
        completedDisputeCount
        completedDisputeAmount
        pendingDisputeCount
        pendingDisputeAmount
        lostDisputeCount
        lostDisputeAmount
      }
    }

    listLocationMetricValues(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iFilter: $iFilter
    ) @include(if: $hasGroupBy) {
      nodes {
        locationId
        metricData
      }
    }

    listLocationGroupDisputes(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iFilter: $iFilter
    ) @skip(if: $hasGroupBy) {
      nodes {
        startDate
        endDate
        locationGroupId
        locationGroupName
        totalDisputeCount
        totalDisputeAmount
        completedDisputeCount
        completedDisputeAmount
        pendingDisputeCount
        pendingDisputeAmount
        lostDisputeCount
        lostDisputeAmount
      }
    }

    listLocationGroupMetricValues(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iFilter: $iFilter
    ) @skip(if: $hasGroupBy) {
      nodes {
        locationGroupId
        metricData
      }
    }
  }
`

export const bbbDisputeMetricsConfigs = {
  ...corporateGroupTableConfigs,
  deliveryOrderWithAnyIssuePercentDoordash: 'percent',
  totalDisputeCount: 'number',
  totalDisputeAmount: 'price',
  completedDisputeCount: 'number',
  completedDisputeAmount: 'price',
  pendingDisputeCount: 'number',
  pendingDisputeAmount: 'price',
  lostDisputeCount: 'number',
  lostDisputeAmount: 'price',
} as const

const YESTERDAY = moment().subtract(1, 'day')

const useBbbDisputeMetrics = () => {
  const { variables } = useVariables()
  const { startDate, endDate: filterEndDate } = useDateFilter()
  const { groupFilter, hasGroupBy } = useGroupFilter()
  const endDate = useMemo(
    () =>
      moment(filterEndDate, 'YYYY-MM-DD').isAfter(YESTERDAY)
        ? YESTERDAY.format('YYYY-MM-DD')
        : filterEndDate,
    [filterEndDate],
  )
  const { data, loading } = useQuery<IDataType>(query, {
    variables: {
      iStartDate: startDate,
      iEndDate: endDate,
      iFilter: {
        location_group_ids: groupFilter?.ids,
        metrics: ['delivery_order_with_any_issue_percent_doordash'],
      },
      hasGroupBy,
    },
    skip: !startDate || !endDate || !groupFilter,
  })

  return {
    data: useMemo((): IApiDataType => {
      const disputeData =
        data?.listLocationDisputes?.nodes ||
        data?.listLocationGroupDisputes?.nodes
      const deliveryData =
        data?.listLocationMetricValues?.nodes ||
        data?.listLocationGroupMetricValues?.nodes
      const corporateDetails = (() => {
        if (!variables.corporateGroup) return

        if (
          'locations' in variables.corporateGroup &&
          variables.corporateGroup.locations
        )
          return variables.corporateGroup.locations
        if (
          'locationGroups' in variables.corporateGroup &&
          variables.corporateGroup.locationGroups
        )
          return variables.corporateGroup.locationGroups
      })()

      if (!disputeData || !deliveryData || !corporateDetails) return null

      return disputeData.map((disputeD) => {
        const id = (() => {
          if ('locationId' in disputeD) return disputeD.locationId
          if ('locationGroupId' in disputeD) return disputeD.locationGroupId
          return -1
        })()
        const deliveryD = (
          deliveryData as {
            locationId?: number
            locationGroupId?: number
            metricData: IMetricDataType
          }[]
        ).find((d) => {
          if ('locationId' in d) return d.locationId === id
          if ('locationGroupId' in d) return d.locationGroupId === id
          return false
        })

        return {
          ...corporateDetails[id]?.tableRow,
          ..._.mapValues(disputeD || {}, parseFloat),
          ...format(deliveryD?.metricData || {}),
          id: id.toString(),
          parentId: 'root',
        }
      })
    }, [data, variables]),
    loading,
  }
}

export default useBbbDisputeMetrics
